import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BadgeCheck, Download, IndianRupee } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr } from "@/lib/format";
import InvoiceDocument from "@/components/InvoiceDocument";
import { Button } from "@/components/ui/button";

export default function PublicInvoice() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/public/documents/${token}`).then(({ data }) => setDoc(data)).catch((e) => setError(apiError(e)));
  }, [token]);

  if (error)
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="text-center" data-testid="public-error">
          <div className="font-heading text-2xl font-bold">Link unavailable</div>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
        </div>
      </div>
    );

  if (!doc) return <div className="min-h-screen grid place-items-center text-sm text-slate-500">Loading…</div>;

  // A credit note is never "due" in the first place, so it reads as settled.
  const settled = Number(doc.balance_due || 0) < 0.01;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="public-invoice-page">
      <header className="bg-slate-900 text-white print:hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-heading text-lg font-bold">{doc.organization?.name}</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-blue-400">
              {doc.document_type === "CREDIT_NOTE" ? "Credit Note" : "Tax Invoice"} · {doc.document_number}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-slate-400">
                {settled ? "Status" : "Amount due"}
              </div>
              {settled ? (
                <div className="flex items-center gap-1.5 font-semibold text-emerald-400"
                  data-testid="public-balance">
                  <BadgeCheck size={16} /> Paid in full
                </div>
              ) : (
                <div className="font-mono text-lg font-bold" data-testid="public-balance">
                  {inr(doc.balance_due)}
                </div>
              )}
            </div>
            <Button size="sm" variant="outline" data-testid="public-print" onClick={() => window.print()}
              className="gap-1.5 bg-white text-slate-900">
              <Download size={15} /> Print / PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6">
        <InvoiceDocument doc={doc} org={doc.organization} />
        {/* doc.upi_uri is the server's answer to "should this invoice ask for
            money at all" — it is null for a settled invoice, a credit note, a
            cancelled document, or an organisation with no UPI id. Asking a
            customer to pay a bill they have already paid is worse than not
            offering the button. */}
        {doc.upi_uri ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-5 print:hidden"
            data-testid="public-pay-block">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Pay by UPI</div>
              <div className="mt-1 font-mono text-sm">{doc.organization?.upi_id}</div>
            </div>
            <a href={doc.upi_uri}
              className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-700"
              data-testid="public-upi-link">
              <IndianRupee size={15} /> Pay {inr(doc.balance_due)}
            </a>
          </div>
        ) : settled ? (
          <div className="mt-4 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-800 print:hidden"
            data-testid="public-paid-block">
            <BadgeCheck size={17} className="shrink-0" />
            <span>
              <strong>Paid in full.</strong> Nothing is outstanding on this
              {doc.document_type === "CREDIT_NOTE" ? " credit note" : " invoice"} —
              keep this page for your records.
            </span>
          </div>
        ) : null}
        <div className="mt-6 text-center text-[11px] text-slate-400 print:hidden">
          Billing by ERP4U · Webteam Private Limited
        </div>
      </main>
    </div>
  );
}
